import type { IHttpClient, HttpRequest } from '@/shared/contracts/http-client.contract';
import { client } from '@/shared/http/client';
import { ApiError, type ApiErrorDetails, type ValidationErrors } from '@/shared/errors/api-error';
import * as Sentry from '@sentry/react';
import { addBreadcrumb } from '@/config/sentry.config';

export class HttpKyClient implements IHttpClient {
  private client: typeof client;

  private constructor(kyClient: typeof client) {
    this.client = kyClient;
  }

  static create(): HttpKyClient {
    return new HttpKyClient(client);
  }

  // Mesmo adaptador sobre outra instância do ky (ex.: o cliente do atendimento,
  // que manda o token do operador — PRAT-64).
  static fromKy(kyClient: typeof client): HttpKyClient {
    return new HttpKyClient(kyClient);
  }

  async sendRequest<TResponse, TBody>(props: HttpRequest<TBody>): Promise<TResponse> {
    const { endpoint, method, body, headers, params } = props;

    // Adicionar breadcrumb antes da requisição
    addBreadcrumb(`HTTP ${method} ${endpoint}`, 'http', 'info', {
      endpoint,
      method,
      hasBody: !!body,
      hasParams: !!params,
    });

    // Iniciar span de performance
    const span = Sentry.startInactiveSpan({
      name: `http.${method.toLowerCase()}`,
      op: 'http.client',
      attributes: {
        'http.method': method,
        'http.url': endpoint,
      },
    });

    try {
      const response = await this.client(endpoint, {
        method: method,
        json: body,
        headers,
        searchParams: params,
      });

      // Adicionar informação de sucesso
      span?.setStatus({ code: 1 }); // OK
      span?.setAttribute('http.status_code', response.status);

      addBreadcrumb(`HTTP ${method} ${endpoint} - ${response.status}`, 'http', 'info', {
        endpoint,
        method,
        status: response.status,
      });

      if (response.status === 204) {
        span?.end();
        return undefined as TResponse;
      }

      const data = await response.json<TResponse>();
      span?.end();
      return data;
    } catch (error: unknown) {
      // Try to extract error message from response
      let errorMessage = 'Erro na requisição';
      let validationErrors: ValidationErrors | undefined;
      let statusCode: number | undefined;
      const details: ApiErrorDetails = {};

      if (error && typeof error === 'object' && 'response' in error) {
        const httpError = error as { response: Response };
        statusCode = httpError.response.status;

        // Atualizar span com erro
        span?.setStatus({ code: 2 }); // ERROR
        span?.setAttribute('http.status_code', statusCode);

        try {
          const errorData = await httpError.response.json();

          if (errorData?.message) {
            errorMessage = errorData.message;
          }

          // Check for validation errors format
          if (errorData?.errors && typeof errorData.errors === 'object') {
            validationErrors = errorData.errors;
          }

          if (typeof errorData?.error_code === 'string') details.code = errorData.error_code;
          if (typeof errorData?.action === 'string') details.action = errorData.action;
        } catch {
          // If can't parse JSON, use status text or generic message
          errorMessage = httpError.response.statusText || 'Erro na requisição';
        }
      } else if (error instanceof Error && !error.message.includes('http')) {
        errorMessage = error.message;
        span?.setStatus({ code: 2 }); // ERROR
      }

      // Adicionar breadcrumb de erro
      addBreadcrumb(
        `HTTP ${method} ${endpoint} - Erro ${statusCode || 'desconhecido'}`,
        'http',
        'error',
        { endpoint, method, statusCode, errorMessage, validationErrors }
      );

      // Capturar erro no Sentry apenas para erros 5xx
      if (statusCode && statusCode >= 500) {
        Sentry.captureException(new Error(`HTTP ${statusCode}: ${errorMessage}`), {
          contexts: {
            http: {
              endpoint,
              method,
              statusCode,
              validationErrors,
            },
          },
        });
      }

      span?.end();
      throw new ApiError(errorMessage, validationErrors, statusCode, details);
    }
  }
}
